import {
	EmailSentEventStatusResponse,
	InvestorMessageRes,
} from '@dealstack/shared/lib/backendClient';
import { Flex, Text, useMantineTheme } from '@mantine/core';
import { Email } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/Email/Email';
import { SessionDetails } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/SessionDetails/SessionDetails';
import { VerticalTimeline } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/VerticalTimeline/VerticalTimeline';
import { StatusDotIcon } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/StatusDotIcon/StatusDotIcon';
import { DetailedMessageHeader } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/DetailedMessageHeader/DetailedMessageHeader';
import {
	hasReadableContent,
	mapStatusToIcon,
} from '@dealstack/frontend/app/pages/Project/MIP/Messages/Messages.utils';
import { useTranslationMipMessages } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { mapStatusToColor } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/StatusWithIcon/StatusWithIcon.utils';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';

interface Props {
	message?: InvestorMessageRes;
	isLoading: boolean;
}

export const DetailedMessage = ({ message, isLoading }: Props) => {
	const theme = useMantineTheme();
	const tMipMessages = useTranslationMipMessages();
	const sendHistory = message?.sendHistory.map((item) => ({
		icon: mapStatusToIcon(item.status),
		text: (
			<Flex gap="0.25rem" align="center">
				<Text>{item.status}</Text>
				<Flex gap="0.25rem" align="center">
					<StatusDotIcon color={mapStatusToColor(item.status)} />
				</Flex>
				<Text>{item.createdAt}</Text>
			</Flex>
		),
	}));

	if (!message && !isLoading) {
		return <EmptyState title={tMipMessages('message.empty')} icon="es-flying-paper" />;
	}

	if (message && !hasReadableContent(message)) {
		return <EmptyState title={tMipMessages('message.not_available')} icon="es-flying-paper" />;
	}

	return (
		<FlexExtended direction="column" w="100%" verticalScroll={true}>
			<FlexExtended py="2rem" px="1rem" direction="column">
				<DetailedMessageHeader
					fullName={message?.senderFullName ?? ''}
					email={message?.senderEmail ?? ''}
					sentDate={message?.sendDate ?? ''}
					status={
						tMipMessages(
							`status.${message?.latestStatus}`
						) as EmailSentEventStatusResponse
					}
					isLoading={isLoading}
				/>
			</FlexExtended>
			<FlexExtended borderTop={`1px solid ${theme.colors.gray[4]}`} h="100%">
				<FlexExtended borderRight={`1px solid ${theme.colors.gray[4]}`} p="1rem" flex={0.6}>
					<Email
						subject={message?.subject ?? ''}
						sentToEmail={message?.recipientEmail ?? ''}
						emailBody={message?.body ?? ''}
						isLoading={isLoading}
					/>
				</FlexExtended>
				<FlexExtended gap="1rem" p="1rem" flex={0.4}>
					<FlexExtended direction="column" gap="1rem" flex={0.5}>
						<Text fw={600}>{tMipMessages('history')}</Text>
						<VerticalTimeline list={sendHistory ?? []} isLoading={isLoading} />
					</FlexExtended>
					<FlexExtended border={`1px solid ${theme.colors.gray[4]}`} flex={0.5}>
						<SessionDetails
							fullName={message?.session?.fullName ?? ''}
							email={message?.session?.email ?? ''}
							startDateAndTime={message?.session?.createdAt ?? ''}
							ipAddress={message?.session?.ipAddress ?? ''}
							location={message?.session?.location ?? ''}
							deviceInfo={message?.session?.deviceInfo ?? ''}
							isLoading={isLoading}
						/>
					</FlexExtended>
				</FlexExtended>
			</FlexExtended>
		</FlexExtended>
	);
};
